import { useTranslation } from 'react-i18next'
import { FileText } from 'lucide-react'
import type { TagDocumentRow } from '@/service/api'

/**
 * Below this a discovery is a suggestion rather than a confirmed reference.
 * Mirrors CONFIDENCE_TWO_KINDS in tag_evidence.py — nothing enforces the pair
 * across the two languages, so a change there must land here too.
 */
const CONFIDENT = 0.92

interface DocumentsPanelProps {
  documents: TagDocumentRow[]
  loading: boolean
  /** `name` so the preview overlay can title itself without a second fetch. */
  /** `mention` is the tag as this document spells it — the preview lands on it. */
  onOpenDocument: (documentId: string, documentName: string, mention?: string | null) => void
}

/**
 * The passages that discuss a tag, grouped by the document they came from.
 *
 * Shows the surrounding text rather than a bare list of filenames: a reviewer
 * deciding whether a discovered tag is real needs to read how it was used, and
 * opening each PDF to find out is the slow path this replaces.
 */
export default function DocumentsPanel({
  documents,
  loading,
  onOpenDocument,
}: DocumentsPanelProps) {
  const { t } = useTranslation()

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        {t('data_catalog:tag_mapping.detail.documents.loading', {
          defaultValue: 'Loading passages…',
        })}
      </div>
    )
  }

  if (documents.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <FileText className="size-6 opacity-50" />
        {t('data_catalog:tag_mapping.detail.documents.empty', {
          defaultValue: 'No document mentions this tag.',
        })}
      </div>
    )
  }

  const byDocument = new Map<string, TagDocumentRow[]>()
  for (const row of documents) {
    const rows = byDocument.get(row.document_id)
    if (rows) rows.push(row)
    else byDocument.set(row.document_id, [row])
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4">
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        {[...byDocument.entries()].map(([documentId, rows]) => (
          <section key={documentId} className="rounded-lg border bg-card">
            <button
              type="button"
              onClick={() => onOpenDocument(documentId, rows[0].document_name, rows[0].mention)}
              className="flex w-full items-center gap-2 border-b px-4 py-3 text-start hover:bg-muted/50"
            >
              <FileText className="size-4 shrink-0 text-muted-foreground" />
              <span className="truncate text-sm font-medium">{rows[0].document_name}</span>
              <span className="ms-auto shrink-0 text-xs text-muted-foreground">
                {t('data_catalog:tag_mapping.detail.documents.passage_count', {
                  count: rows.length,
                  defaultValue: '{{count}} passages',
                })}
              </span>
            </button>

            <ul className="flex flex-col divide-y">
              {rows.map((row, index) => (
                <li key={`${documentId}-${index}`} className="px-4 py-3">
                  <p className="text-sm leading-relaxed text-muted-foreground">{row.context}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{row.mention}</code>
                    {row.confidence < CONFIDENT && (
                      <span className="text-xs text-muted-foreground">
                        {t('data_catalog:tag_mapping.detail.documents.suggested', {
                          defaultValue: 'Suggested',
                        })}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  )
}
